import { Component, ElementRef, HostListener, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Channel, ChannelFns } from 'icreate/common/models/channel';
import {
    AlarmPropertyList,
    NonDigitalDataType,
    NonDigitalValueFlag,
    ReposeGroup,
} from 'icreate/common/models/repose-group';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { reposeGroupValidator } from 'icreate/common/validators/repose-group/repose-group-validator';
import { lastValueFrom, take } from 'rxjs';

import _ from 'lodash';
import { defaultAlarmDropDownItem } from '@models/repose-group';
import { ChannelService } from '@service/channel/channel.service';
import { ReposeGroupHelperService } from '@service/repose-group-helper.service';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';

enum ReposeGroupInputName {
    NAME = 'name',
    DESCRIPTION = 'description',
    SETPOINT = 'setPoint',
    CHANNEL_STATUS = 'channelStatus',
    START_DELAY = 'startDelay',
    STOP_DELAY = 'stopDelay',
}

export enum ReposeGroupStep {
    STEP1 = 1,
    STEP2 = 2,
}

@Component({
    selector: 'app-repose-dialog',
    templateUrl: './repose-dialog.component.html',
    styleUrls: ['./repose-dialog.component.css'],
    standalone: false,
})
export class ReposeDialogComponent {
    readonly NAME_INPUT = ReposeGroupInputName.NAME;
    readonly DESCRIPTION_INPUT = ReposeGroupInputName.DESCRIPTION;
    readonly SETPOINT = ReposeGroupInputName.SETPOINT;
    readonly CHANNEL_STATUS = ReposeGroupInputName.CHANNEL_STATUS;
    readonly START_DELAY = ReposeGroupInputName.START_DELAY;
    readonly STOP_DELAY = ReposeGroupInputName.STOP_DELAY;

    readonly STEP1 = ReposeGroupStep.STEP1;
    readonly STEP2 = ReposeGroupStep.STEP2;

    readonly NON_DIGITAL_FLAG_VALUE = NonDigitalDataType.VALUE;
    readonly NON_DIGITAL_STATUS = NonDigitalDataType.STATUS;

    readonly NON_DIGITAL_VALUE_FLAG_EXCEED = NonDigitalValueFlag.EXCEED;
    readonly NON_DIGITAL_VALUE_FLAG_FALL = NonDigitalValueFlag.FALL;

    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;

    readonly DELAY_UNIT = 'sec';

    @ViewChild('listContainer') listContainer: ElementRef;

    validationErrors: ValidationErrorMap = {};
    currentStep: ReposeGroupStep = this.STEP1;
    assignedChannel: Channel = null;
    nonDigitalDataType: NonDigitalDataType = NonDigitalDataType.NULL;
    nonDigitalValueFlag: NonDigitalValueFlag = NonDigitalValueFlag.NULL;

    isShowDropDown: boolean = false;
    alarmDropDownList = defaultAlarmDropDownItem;
    selectedAlarmDropDownItem;

    editMode: boolean = false;
    isLoading: boolean = true;
    disableGoBack: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            id: string;
            name: string;
            description: string;
            vesselId: string;
            channelId: string;
            nonDigitalDataType: NonDigitalDataType;
            nonDigitalValueFlag: NonDigitalValueFlag;
            setPoint: string;
            nonDigitalAlarm: string;
            digitalChannelStatus: string;
            startDelay: string | number;
            stopDelay: string | number;
            groupNo: number;
            currentStep: ReposeGroupStep;
            reposeGroupList: ReposeGroup[];
            disableGoBack: boolean;
        },
        private translate: TranslateService,
        private dialogRef: MatDialogRef<ReposeDialogComponent>,
        private dialog: MatDialog,
        private channelService: ChannelService,
        private reposeGroupHelperService: ReposeGroupHelperService,
        private keyFilterService: KeyFilterSerivce
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (this.listContainer?.nativeElement.contains(event.target)) {
            this.isShowDropDown = !this.isShowDropDown;
        } else if (!this.listContainer?.nativeElement.contains(event.target) && this.isShowDropDown) {
            this.isShowDropDown = false;
        }
    }

    async ngOnInit() {
        if (this.data?.currentStep) {
            this.currentStep = this.data.currentStep;
            this.editMode = true;
        }

        if (this.data?.nonDigitalDataType) {
            this.nonDigitalDataType = this.data.nonDigitalDataType;
        }

        if (this.data?.nonDigitalValueFlag) {
            this.nonDigitalValueFlag = this.data.nonDigitalValueFlag;
        }

        if (this.data?.nonDigitalAlarm) {
            const findItem = defaultAlarmDropDownItem.find(item => item.propertyName === this.data?.nonDigitalAlarm);
            this.selectedAlarmDropDownItem = findItem;
        }

        if (this.data?.channelId) {
            this.assignedChannel = await lastValueFrom(this.channelService.getById(this.data.channelId));
        }

        if (this.data?.disableGoBack) {
            this.disableGoBack = this.data.disableGoBack;
        }

        this.checkInputErrors();
        this.isLoading = false;
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.NAME_INPUT) {
            this.data.name = event.value;
        } else if (event.name === this.SETPOINT) {
            this.data.setPoint = event.value;
        } else if (event.name === this.CHANNEL_STATUS) {
            this.data.digitalChannelStatus =
                event.value === this.assignedChannel.fcuData.digitalAlarmSetting.statusStringContactOn ? '1' : '0';
        } else if (event.name === this.START_DELAY) {
            this.data.startDelay = event.value;
        } else if (event.name === this.STOP_DELAY) {
            this.data.stopDelay = event.value;
        } else if (event.name === this.DESCRIPTION_INPUT) {
            this.data.description = event.value;
        }

        this.checkInputErrors();
    }

    onTextAreaChange(value) {
        this.data.description = value;
        this.checkInputErrors();
    }

    checkInputErrors() {
        const { name, description, setPoint, startDelay, stopDelay, digitalChannelStatus, reposeGroupList } = this.data;

        const parsedSetPoint = setPoint !== '' ? Number(setPoint) : null;
        const object = {
            name,
            description,
            setPoint: parsedSetPoint,
            startDelay: startDelay !== '' ? Number(startDelay) : 0,
            stopDelay: stopDelay !== '' ? Number(stopDelay) : 0,
            digitalChannelStatus: digitalChannelStatus !== '' ? Number(digitalChannelStatus) : null,
        };

        const errors = getValidationErrors(reposeGroupValidator(), object);
        let validationErrors: ValidationErrorMap | null = errors ? mapErrors(errors) : null;

        const range = this.assignedChannel?.fcuData?.measurableRange;
        const unit = this.assignedChannel?.opsData?.measurementUnit;

        if (
            range &&
            parsedSetPoint !== null &&
            (parsedSetPoint < range.min || parsedSetPoint > range.max) &&
            !validationErrors?.setPoint?.message
        ) {
            const message = this.translate.instant('repose-group.dialog.set-point.error.message', {
                min: range.min,
                max: range.max,
                unit,
            });

            validationErrors = {
                ...validationErrors,
                setPoint: { message },
            };
        }

        if (!validationErrors?.name) {
            const isDuplicate = reposeGroupList.some(group => group.name === name && group.id !== this.data.id);
            if (isDuplicate) {
                validationErrors = {
                    ...validationErrors,
                    name: {
                        message: 'common.error.name-duplicate',
                    },
                };
            }
        }
        this.validationErrors = validationErrors;
    }

    goNextStep() {
        this.currentStep = this.STEP2;
    }

    backStep1() {
        this.currentStep = this.STEP1;
    }

    async openSearchChannelDialog() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.data.vesselId,
                assignedChannel: this.assignedChannel,
                allowedTypesSet: undefined,
                setShouldDisableTypes: false,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (channelSelected) {
            if (!_.isEqual(this.assignedChannel, channelSelected)) {
                this.nonDigitalDataType = NonDigitalDataType.NULL;
                this.nonDigitalValueFlag = NonDigitalValueFlag.NULL;
                this.data.setPoint = '';
                this.data.nonDigitalAlarm = '';
                this.selectedAlarmDropDownItem = null;
                this.data.digitalChannelStatus = '';

                this.checkInputErrors();
            }
            this.assignedChannel = channelSelected;
            this.createDropDownItem();
        }
    }

    createDropDownItem() {
        Object.values(AlarmPropertyList).forEach(alarmPropertyName => {
            if (this.assignedChannel.fcuData[alarmPropertyName]?.enable) {
                const findIndex = defaultAlarmDropDownItem.findIndex(item => item.propertyName === alarmPropertyName);
                if (findIndex !== -1) {
                    const setPoint = this.assignedChannel.fcuData[alarmPropertyName].setPoint;
                    this.alarmDropDownList[findIndex].setPoint = setPoint ? setPoint : null;
                }
            } else {
                const findIndex = defaultAlarmDropDownItem.findIndex(item => item.propertyName === alarmPropertyName);
                if (findIndex !== -1) {
                    this.alarmDropDownList[findIndex].setPoint = null;
                }
            }
        });
    }

    getMeasurableRangeStr() {
        return (
            this.assignedChannel.fcuData.measurableRange.min +
            ' ' +
            this.assignedChannel.opsData.measurementUnit +
            ' - ' +
            this.assignedChannel.fcuData.measurableRange.max +
            ' ' +
            this.assignedChannel.opsData.measurementUnit
        );
    }

    getNormalRangeStr() {
        return (
            this.assignedChannel.fcuData.normalRange.min +
            ' ' +
            this.assignedChannel.opsData.measurementUnit +
            ' - ' +
            this.assignedChannel.fcuData.normalRange.max +
            ' ' +
            this.assignedChannel.opsData.measurementUnit
        );
    }

    getAlarmRangeStr(alarmProperty: string) {
        return (
            this.assignedChannel.fcuData[alarmProperty].setPoint + ' ' + this.assignedChannel.opsData.measurementUnit
        );
    }

    close() {
        this.dialogRef.close();
    }

    isDigitalChannel() {
        if (this.assignedChannel) {
            return ChannelFns.isDigital(this.assignedChannel);
        }

        return false;
    }

    setNonDigitalDataType(flag: NonDigitalDataType) {
        if (this.nonDigitalDataType !== flag) {
            this.nonDigitalValueFlag = NonDigitalValueFlag.NULL;
            this.data.setPoint = '';
            this.data.nonDigitalAlarm = '';
            this.selectedAlarmDropDownItem = null;
            this.data.digitalChannelStatus = '';
            this.data.startDelay = null;
            this.data.stopDelay = null;
            this.checkInputErrors();
        }
        this.nonDigitalDataType = flag;
    }

    setNonDigitalValueFlag(flag: NonDigitalValueFlag) {
        this.nonDigitalValueFlag = flag;
    }

    showDropDown() {
        this.isShowDropDown = !this.isShowDropDown;
    }

    selectAlarmItem(item) {
        this.data.nonDigitalAlarm = item.propertyName;
        this.selectedAlarmDropDownItem = item;
    }

    disabledNormalRange() {
        let count = 0;
        Object.values(AlarmPropertyList).forEach(alarmPropertyName => {
            if (this.assignedChannel.fcuData[alarmPropertyName]?.enable) {
                count++;
            }
        });
        return count === 0;
    }

    setNormalAlarmItemName(item, index) {
        let name = '';
        if (item?.name) {
            name = this.translate.instant(item.name);
        }
        if (index === 0) {
            return name;
        } else {
            let message = this.translate.instant('repose-group.dialog.status.not-enable.message');
            return this.assignedChannel.fcuData[item.propertyName]?.enable ? name : name + message;
        }
    }

    async createReposeGroup() {
        try {
            this.reposeGroupHelperService.showLoading();
            this.dialogRef.close({
                reposeGroup: {
                    ...this.createReposeObj(),
                    id: this.data.id,
                },
                close: true,
            });
            this.reposeGroupHelperService.groupUpdated();
        } catch (error) {
            console.log(error);
        } finally {
            this.reposeGroupHelperService.hideLoading();
        }
    }

    async goBackReview() {
        try {
            this.reposeGroupHelperService.showLoading();
            this.dialogRef.close({
                reposeGroup: {
                    ...this.createReposeObj(),
                    id: this.data.id,
                },
                channel: this.assignedChannel,
                close: false,
            });
        } catch (error) {
            console.log(error);
        } finally {
            this.reposeGroupHelperService.hideLoading();
        }
    }

    editReposeGroup() {
        this.dialogRef.close({
            reposeGroup: {
                ...this.createReposeObj(),
                id: this.data.id,
            },
            channel: this.assignedChannel,
            close: true,
        });
    }

    isStep1SaveButtonDisabled() {
        return !this.data?.name || this.validationErrors?.name?.message || this.validationErrors?.description?.message;
    }
    createReposeObj(): Omit<ReposeGroup, 'id'> & Partial<Pick<ReposeGroup, 'id'>> {
        return {
            name: this.data.name,
            description: this.data.description,
            groupNo: this.data.groupNo,
            vesselId: this.data.vesselId,
            startDelay: Number(this.data.startDelay),
            stopDelay: Number(this.data.stopDelay),
            setPoint: Number(this.data.setPoint),
            digitalChannelStatus: Number(this.data.digitalChannelStatus),
            nonDigitalDataType: this.nonDigitalDataType,
            nonDigitalValueFlag: this.nonDigitalValueFlag,
            nonDigitalAlarm: this.data.nonDigitalAlarm,
            channelId: this.assignedChannel.id,
        };
    }

    public get validatorFunction() {
        return reposeGroupValidator;
    }

    validDelayKeyDown(eventObj: { event: KeyboardEvent; value }) {
        const { event, value } = eventObj;
        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, this.ALLOWED_KEYS);
        const functionalKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

        if (shouldBlockKey) event.preventDefault();

        if (functionalKeys.includes(event.key)) {
            return;
        }
        if (value.length >= 4) {
            event.preventDefault();
        }
    }

    validInputValueKeyDown(eventObj: { event: KeyboardEvent; value }) {
        const { event } = eventObj;
        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, [...this.ALLOWED_KEYS, '-']);

        if (shouldBlockKey) event.preventDefault();
    }
}
